"use client";

import { ReactNode } from "react";

import { BreadcrumbItem, Breadcrumbs } from "@heroui/breadcrumbs";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";

interface BreadcrumbItemProps {
  /** Omit for non-navigable crumbs (e.g. doc section with no index page). */
  href?: string;
  label: string;
  icon?: ReactNode;
}

interface PageBreadcrumbsProps {
  className?: string;
  items: BreadcrumbItemProps[];
  ariaLabel: string;
  "data-testid"?: string;
}

export default function PageBreadcrumbs({
  ariaLabel = "Breadcrumb",
  className = "",
  "data-testid": dataTestId,
  items,
}: PageBreadcrumbsProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className={`mb-6 ${className}`}
      data-testid={dataTestId}
    >
      <Breadcrumbs
        classNames={{
          base: "w-full",
          list: "w-full max-w-full",
        }}
        color="primary"
        underline="hover"
        variant="solid"
      >
        {items.map((item, index) => (
          <BreadcrumbItem
            key={item.href ?? `crumb-${index}-${item.label}`}
            classNames={{
              item: "flex px-2 hover:text-primary transition-colors",
            }}
            {...(item.href != null && item.href !== ""
              ? { href: item.href }
              : {})}
            isCurrent={index === items.length - 1}
            startContent={
              item.icon ? (
                <span aria-hidden="true" className="mr-2">
                  {item.icon}
                </span>
              ) : null
            }
            {...(index === items.length - 1 ? { "aria-current": "page" } : {})}
            onClick={() => {
              if (index !== items.length - 1 && item.href) {
                void EventPublisher.publishEvent(
                  EventKeys.BREADCRUMB_NAV_CLICK,
                  {
                    href: item.href,
                    index,
                    label: item.label,
                  },
                );
              }
            }}
          >
            {item.label}
          </BreadcrumbItem>
        ))}
      </Breadcrumbs>
    </nav>
  );
}
